export const FILTER_TYPES = [
  { value: "text", label: "Text" },
  { value: "select", label: "Select" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date-range", label: "Date range" },
  { value: "autocomplete", label: "Autocomplete" },
  { value: "time-range", label: "Time range" },
] as const

export type FilterType = (typeof FILTER_TYPES)[number]["value"]

export function isFilterType(value: string | undefined): value is FilterType {
  return FILTER_TYPES.some((type) => type.value === value)
}

export const FILTER_DEFAULTS = {
  isCollection: true,
  selectMultiple: false,
  autocompleteMultiple: true,
  allowRange: true,
  selectParseAs: "string",
} as const

export interface FilterOption {
  name?: string
  value?: string | number | boolean
  label?: string
  diff?: number
}

export interface FilterConfig {
  type?: string
  description?: string
  helpText?: string
  isGroupEnd?: boolean
  /** @deprecated kept only so existing values are preserved, never edited */
  order?: number
  /** Retention-only, never edited */
  filterGroup?: string
  maxLength?: number
  placeholder?: string
  defaultValue?: string | string[] | number | boolean
  options?: FilterOption[]
  multiple?: boolean
  emptyLabel?: string
  parseAs?: "literal" | "string"
  min?: number
  max?: number
  allowRange?: boolean
}

const SECOND = 1000
export const MS_PER_DAY = 24 * 60 * 60 * SECOND
export const MS_PER_WEEK = 7 * MS_PER_DAY
export const MS_PER_MONTH = 4 * MS_PER_WEEK

export type DurationUnit = "months" | "weeks" | "days" | "milliseconds"

export const DURATION_UNITS: Array<{ value: DurationUnit; label: string }> = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
]

const UNIT_MS: Record<DurationUnit, number> = {
  months: MS_PER_MONTH,
  weeks: MS_PER_WEEK,
  days: MS_PER_DAY,
  milliseconds: 1,
}

export function durationToMs(amount: number, unit: DurationUnit): number {
  return Math.round(amount * UNIT_MS[unit])
}

export function msToDuration(ms: number): { amount: number; unit: DurationUnit } {
  if (ms > 0) {
    for (const unit of ["months", "weeks", "days"] as const) {
      if (ms % UNIT_MS[unit] === 0) return { amount: ms / UNIT_MS[unit], unit }
    }
  }
  return { amount: ms, unit: "milliseconds" }
}

export function formatDuration(ms: number): string {
  const { amount, unit } = msToDuration(ms)
  const singular = unit.slice(0, -1)
  return `${amount} ${amount === 1 ? singular : unit}`
}

export interface SelectOptionDraft {
  value: string | number
  label: string
}

export interface PresetDraft {
  name: string
  amount: string
  unit: DurationUnit
}

export interface FilterDraft {
  // text
  maxLength: string
  placeholder: string
  textDefault: string
  // select
  options: SelectOptionDraft[]
  selectMultiple: boolean
  autocompleteMultiple: boolean
  emptyLabel: string
  selectDefaults: string[]
  parseAs: "literal" | "string"
  // number
  min: string
  max: string
  numberDefault: string
  // boolean
  trueLabel: string
  falseLabel: string
  booleanDefault: "" | "true" | "false"
  // date-range
  allowRange: boolean
  presets: PresetDraft[]
}

export function draftFromConfig(config: FilterConfig | undefined, type: FilterType | ""): FilterDraft {
  const same = config?.type === type
  const c = same ? config : undefined
  const options = c?.options ?? []
  const defaultValues = Array.isArray(c?.defaultValue)
    ? c.defaultValue.map(String)
    : typeof c?.defaultValue === "string" && type === "select" && c.defaultValue.length > 0
      ? [c.defaultValue]
      : []

  return {
    maxLength: c?.maxLength != null ? String(c.maxLength) : "",
    placeholder: c?.placeholder ?? "",
    textDefault: type === "text" && typeof c?.defaultValue === "string" ? c.defaultValue : "",
    options:
      type === "select"
        ? options.map((o) => ({ value: (o.value as string | number) ?? "", label: o.label ?? "" }))
        : [],
    selectMultiple: c?.multiple ?? FILTER_DEFAULTS.selectMultiple,
    autocompleteMultiple: c?.multiple ?? FILTER_DEFAULTS.autocompleteMultiple,
    emptyLabel: c?.emptyLabel ?? "",
    selectDefaults: defaultValues,
    parseAs: c?.parseAs ?? FILTER_DEFAULTS.selectParseAs,
    min: c?.min != null ? String(c.min) : "",
    max: c?.max != null ? String(c.max) : "",
    numberDefault: type === "number" && typeof c?.defaultValue === "number" ? String(c.defaultValue) : "",
    trueLabel: type === "boolean" ? (options.find((o) => o.value === true)?.label ?? "") : "",
    falseLabel: type === "boolean" ? (options.find((o) => o.value === false)?.label ?? "") : "",
    booleanDefault:
      type === "boolean" && typeof c?.defaultValue === "boolean" ? (c.defaultValue ? "true" : "false") : "",
    allowRange: c?.allowRange ?? FILTER_DEFAULTS.allowRange,
    presets:
      type === "date-range"
        ? options.map((o) => {
            const { amount, unit } = msToDuration(o.diff ?? 0)
            return { name: o.name ?? "", amount: String(amount), unit }
          })
        : [],
  }
}

export type BuildResult<T> = { ok: true; value: T } | { ok: false; error: string }

const fail = (error: string): { ok: false; error: string } => ({ ok: false, error })

function parseOptionalNumber(raw: string, label: string): BuildResult<number | undefined> {
  const text = raw.trim()
  if (text.length === 0) return { ok: true, value: undefined }
  const value = Number(text)
  if (!Number.isFinite(value)) return fail(`${label} must be a number.`)
  return { ok: true, value }
}

export function buildTypeProperties(
  type: FilterType,
  draft: FilterDraft,
  original?: FilterConfig,
): BuildResult<FilterConfig> {
  const wasSet = (key: keyof FilterConfig) => original?.type === type && original[key] !== undefined
  const out: FilterConfig = {}

  switch (type) {
    case "text": {
      const maxLength = parseOptionalNumber(draft.maxLength, "Max length")
      if (!maxLength.ok) return maxLength
      if (maxLength.value != null) out.maxLength = maxLength.value
      if (draft.placeholder.length > 0) out.placeholder = draft.placeholder
      if (draft.textDefault.length > 0) out.defaultValue = draft.textDefault
      return { ok: true, value: out }
    }

    case "select": {
      const values = draft.options.map((o) => String(o.value ?? "").trim())
      if (values.length === 0) return fail("Add at least one option before saving.")
      if (values.some((v) => v.length === 0)) {
        return fail("Enter a value for each option, or remove any empty options before saving.")
      }
      if (new Set(values).size !== values.length) {
        return fail("Each option needs a different value. Change or remove the duplicate before saving.")
      }
      out.options = draft.options.map((o, i) => {
        const label = o.label.trim()
        const originalValue = typeof o.value === "number" ? o.value : values[i]
        return label.length > 0 ? { value: originalValue, label } : { value: originalValue }
      })
      if (draft.selectMultiple || wasSet("multiple")) out.multiple = draft.selectMultiple
      if (draft.emptyLabel.length > 0) out.emptyLabel = draft.emptyLabel
      if (draft.parseAs !== FILTER_DEFAULTS.selectParseAs || wasSet("parseAs")) out.parseAs = draft.parseAs

      if (draft.selectDefaults.length > 0) {
        const missing = draft.selectDefaults.filter((d) => !values.includes(d))
        if (missing.length > 0) {
          return fail(
            `Default value "${missing[0]}" is not one of the options. Choose a listed option or clear the default.`,
          )
        }
        if (!draft.selectMultiple && draft.selectDefaults.length > 1) {
          return fail("Only one default value is allowed unless multiple selection is on.")
        }
        out.defaultValue = draft.selectMultiple ? draft.selectDefaults : draft.selectDefaults[0]
      }
      return { ok: true, value: out }
    }

    case "number": {
      const min = parseOptionalNumber(draft.min, "Min")
      if (!min.ok) return min
      const max = parseOptionalNumber(draft.max, "Max")
      if (!max.ok) return max
      const def = parseOptionalNumber(draft.numberDefault, "Default value")
      if (!def.ok) return def
      if (min.value != null && max.value != null && min.value > max.value) {
        return fail("Min must be less than or equal to Max.")
      }
      if (
        def.value != null &&
        ((min.value != null && def.value < min.value) || (max.value != null && def.value > max.value))
      ) {
        return fail("Default value must be within the configured limits.")
      }
      if (min.value != null) out.min = min.value
      if (max.value != null) out.max = max.value
      if (def.value != null) out.defaultValue = def.value
      return { ok: true, value: out }
    }

    case "boolean": {
      const trueLabel = draft.trueLabel.trim()
      const falseLabel = draft.falseLabel.trim()
      if (trueLabel.length === 0 || falseLabel.length === 0) {
        return fail("A boolean filter needs a label for both the true and the false option.")
      }
      out.options = [
        { value: true, label: trueLabel },
        { value: false, label: falseLabel },
      ]
      if (draft.placeholder.length > 0) out.placeholder = draft.placeholder
      if (draft.booleanDefault !== "") out.defaultValue = draft.booleanDefault === "true"
      return { ok: true, value: out }
    }

    case "date-range": {
      if (!draft.allowRange || wasSet("allowRange")) out.allowRange = draft.allowRange
      if (draft.presets.length > 0) {
        const names = draft.presets.map((p) => p.name.trim())
        if (names.some((n) => n.length === 0)) return fail("Give each quick-pick preset a name.")
        if (new Set(names).size !== names.length) return fail("Each quick-pick preset needs a different name.")
        const options: FilterOption[] = []
        for (const [i, preset] of draft.presets.entries()) {
          const amount = Number(preset.amount)
          if (preset.amount.trim().length === 0 || !Number.isFinite(amount)) {
            return fail(`Preset "${names[i]}" needs an amount.`)
          }
          options.push({ name: names[i], diff: durationToMs(amount, preset.unit) })
        }
        out.options = options
      }
      return { ok: true, value: out }
    }

    case "autocomplete": {
      if (!draft.autocompleteMultiple || wasSet("multiple")) out.multiple = draft.autocompleteMultiple
      return { ok: true, value: out }
    }

    case "time-range":
      return { ok: true, value: out }
  }
}

export interface CommonFilterProps {
  description: string
  helpText: string
  isGroupEnd: boolean
}

export function buildFilterConfig(
  type: FilterType,
  common: CommonFilterProps,
  draft: FilterDraft,
  displayName: string,
  original?: FilterConfig,
): BuildResult<FilterConfig> {
  const typed = buildTypeProperties(type, draft, original)
  if (!typed.ok) return typed

  const description = common.description.trim().length > 0 ? common.description.trim() : displayName.trim()
  const config: FilterConfig = { type, description }
  if (common.helpText.trim().length > 0) config.helpText = common.helpText.trim()
  if (common.isGroupEnd || original?.isGroupEnd !== undefined) config.isGroupEnd = common.isGroupEnd
  if (original?.order !== undefined) config.order = original.order
  if (original?.filterGroup !== undefined) config.filterGroup = original.filterGroup

  return { ok: true, value: { ...config, ...typed.value } }
}
